/**
 * Generated by orval v7.9.0 🍺
 * Do not edit manually.
 * Meeting BaaS API
 * API for managing meeting bots, calendar integrations, and webhooks
 * OpenAPI spec version: 2.0.0
 */

import type { BodyType } from "../../../../custom-axios"

import { customInstance } from "../../../../custom-axios"
import type {
  CreateCalendarBotRequestBodyInput,
  CreateCalendarBotResponse,
  CreateCalendarConnectionRequestBodyInput,
  CreateCalendarConnectionResponse,
  DeleteCalendarBotBody,
  DeleteCalendarBotResponse,
  DeleteCalendarConnectionResponse,
  GetCalendarDetailsResponse,
  GetEventDetailsResponse,
  ListCalendarsParams,
  ListCalendarsResponse,
  ListEventSeriesParams,
  ListEventSeriesResponse,
  ListEventsParams,
  ListEventsResponse,
  ListRawCalendarsRequestBodyInput,
  ListRawCalendarsResponse,
  ResubscribeCalendarResponse,
  SyncCalendarResponse,
  UpdateCalendarBotRequestBodyInput,
  UpdateCalendarBotResponse,
  UpdateCalendarConnectionRequestBodyInput,
  UpdateCalendarConnectionResponse
} from "../../schema"

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1]

/**
 * Preview available calendars from a Google or Microsoft account before creating a connection.
    
    Requires OAuth credentials (client ID, client secret, refresh token) to authenticate and list calendars. This endpoint does not create a connection - it only lists the calendars that are available for the given OAuth credentials. Useful for allowing users to select which calendars to sync.
    
    **OAuth Credentials:** You must provide valid OAuth credentials for the calendar provider. The endpoint will use the refresh token to obtain an access token and list calendars. If the refresh token is invalid or expired, the request will fail with 401 Unauthorized.
    
    **Calendar Information:** Returns a list of calendars with their IDs, names, descriptions, and whether they are primary calendars. Calendar IDs differ between providers (Google uses email-like IDs, Microsoft uses GUIDs).
    
    **Use Case:** This endpoint is typically called before creating a calendar connection to show users which calendars are available. Users can then select which calendars they want to sync.
    
    Returns 401 if OAuth token refresh failed, or 403 if a Microsoft account license is required.
 * @summary List raw calendars (preview before creating connection)
 */
export const listRawCalendars = (
  listRawCalendarsRequestBodyInput: BodyType<ListRawCalendarsRequestBodyInput>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ListRawCalendarsResponse>(
    {
      url: "/v2/calendars/list-raw",
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: listRawCalendarsRequestBodyInput
    },
    options
  )
}
/**
 * Connect a Google or Microsoft calendar to your account.
    
    The connection will automatically sync events and create push subscriptions for real-time updates. You must provide your own OAuth credentials (client ID, secret, refresh token). Once connected, the calendar will be synced immediately, and webhook subscriptions will be created for real-time event updates.
    
    **OAuth Credentials:** You must provide valid OAuth credentials for the calendar provider. The endpoint will validate the credentials by attempting to refresh the access token. If the refresh token is invalid or expired, the request will fail with 401 Unauthorized.
    
    **Initial Sync:** After creating the connection, an initial sync is performed automatically. This fetches all events from the calendar provider. The sync may take a few minutes for calendars with many events.
    
    **Push Subscriptions:** A push subscription is created automatically for real-time event updates. The subscription will send webhooks when events are created, updated, or cancelled.
    
    **Calendar Limits:** There may be limits on the number of calendar connections per team. If the limit is exceeded, the request will fail with 429 Status Code.
    
    **Duplicate Connections:** If a connection already exists for the same calendar ID and team, the request will fail with 409 Conflict. You can update an existing connection using the PATCH endpoint instead.
    
    Returns 201 with the newly created calendar connection. Returns 401 if OAuth token refresh failed, 429 if the calendar connection limit is exceeded, or 409 if the connection already exists.
 * @summary Create calendar connection
 */
export const createCalendarConnection = (
  createCalendarConnectionRequestBodyInput: BodyType<CreateCalendarConnectionRequestBodyInput>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<CreateCalendarConnectionResponse>(
    {
      url: "/v2/calendars",
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: createCalendarConnectionRequestBodyInput
    },
    options
  )
}
/**
 * Retrieve a paginated list of calendar connections.
    
    Supports filtering by calendar platform (google, microsoft) and connection status (active, error, revoked, permission_denied). Results are ordered by creation date (newest first). Use cursor-based pagination for efficient navigation.
    
    **Pagination:** Uses cursor-based pagination. Provide a `cursor` query parameter to fetch the next page. The `limit` parameter controls how many results are returned per page (default: 50, max: 250).
    
    **Filtering:**
    - `platform`: Filter by calendar platform (google, microsoft)
    - `status`: Filter by connection status (active, error, revoked, permission_denied)
    
    **Connection Status:**
    - `active`: Connection is working and syncing events
    - `error`: Connection has errors (OAuth token refresh failed, etc.)
    - `revoked`: OAuth access was revoked by the user
    - `permission_denied`: Insufficient permissions for the OAuth scopes
    
    Returns a paginated list of calendar connections with metadata including calendar ID, platform, account email, status, and last sync time.
 * @summary List calendar connections
 */
export const listCalendars = (
  params: ListCalendarsParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ListCalendarsResponse>(
    { url: "/v2/calendars", method: "GET", params },
    options
  )
}
/**
 * Retrieve detailed information about a specific calendar connection.
    
    Returns the calendar connection's configuration, sync status, subscription status, and last sync time. Includes information about the OAuth credentials (without exposing sensitive data) and the calendar's metadata.
    
    **Sync Status:** The response includes the last sync time and whether the connection is actively syncing. If the connection has errors, the error information is included.
    
    **Subscription Status:** Includes information about the push subscription, including when it was created and when it expires. Subscriptions expire after a certain period and need to be renewed using the resubscribe endpoint.
    
    **Calendar Metadata:** Includes the calendar's ID, name, platform, and account email. This information is fetched from the calendar provider during the initial sync.
    
    Returns 404 if the calendar connection is not found or does not belong to your team.
 * @summary Get calendar connection details
 */
export const getCalendarDetails = (
  calendarId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<GetCalendarDetailsResponse>(
    { url: `/v2/calendars/${calendarId}`, method: "GET" },
    options
  )
}
/**
 * Disconnect and delete a calendar connection.
    
    This will stop syncing events and remove all associated calendar data (events, event instances, series). The push subscription will be cancelled automatically. This operation is irreversible.
    
    **Data Deletion:** All calendar data associated with this connection will be deleted:
    - Event series and instances
    - Calendar bot schedules
    - Sync history
    
    **Subscription Cancellation:** The push subscription is cancelled automatically when the connection is deleted. You will no longer receive webhooks for this calendar.
    
    **Irreversible Operation:** Once a calendar connection is deleted, it cannot be recovered. All associated data is permanently removed. If you need to reconnect the calendar, you must create a new connection.
    
    **Bot Schedules:** If there are active calendar bot schedules for events in this calendar, they will be cancelled when the connection is deleted. Bots that have already joined meetings will continue to function normally.
    
    Returns 200 with confirmation of deletion. Returns 404 if the calendar connection is not found or does not belong to your team.
 * @summary Delete calendar connection
 */
export const deleteCalendarConnection = (
  calendarId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<DeleteCalendarConnectionResponse>(
    { url: `/v2/calendars/${calendarId}`, method: "DELETE" },
    options
  )
}
/**
 * Update a calendar connection with new OAuth credentials.
    
    Useful when refresh tokens expire or credentials need to be rotated. The connection will be validated and a new push subscription will be created. The old subscription will be cancelled automatically.
    
    **OAuth Credentials:** You can update the client ID, client secret, and refresh token. All fields are optional - only provided fields will be updated. The endpoint will validate the new credentials by attempting to refresh the access token.
    
    **Validation:** After updating credentials, the connection is validated by attempting to refresh the access token. If the refresh fails, the connection status is updated to `error` and the request may fail with 401 Unauthorized.
    
    **Subscription Renewal:** A new push subscription is created automatically after updating credentials. The old subscription is cancelled to prevent duplicate webhooks.
    
    **Use Cases:**
    - Refresh token expired and needs to be renewed
    - OAuth credentials rotated for security
    - Fixing connection errors by updating credentials
    
    Returns 200 with the updated calendar connection. Returns 401 if OAuth token refresh failed, 403 if permission is denied, or 404 if the calendar connection is not found.
 * @summary Update calendar connection
 */
export const updateCalendarConnection = (
  calendarId: string,
  updateCalendarConnectionRequestBodyInput: BodyType<UpdateCalendarConnectionRequestBodyInput>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<UpdateCalendarConnectionResponse>(
    {
      url: `/v2/calendars/${calendarId}`,
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      data: updateCalendarConnectionRequestBodyInput
    },
    options
  )
}
/**
 * Manually trigger a sync of calendar events.
    
    This will fetch all events from the calendar provider and update the calendar data. Events are normally synced automatically via push subscriptions, but you can use this endpoint to force a sync (e.g., after fixing connection errors or when you need immediate updates).
    
    **Sync Process:** The sync process fetches all events from the calendar provider. New events are added, updated events are modified, and cancelled events are marked as deleted. The sync may take a few minutes for calendars with many events.
    
    **Incremental vs Full Sync:** The endpoint performs a full sync, fetching all events from the calendar. Incremental syncs happen automatically via push subscriptions when events are created, updated, or cancelled.
    
    **Use Cases:**
    - Force a sync after fixing connection errors
    - Get immediate updates without waiting for push notifications
    - Recover from missed push notifications
    - Initial sync after creating a connection (though this happens automatically)
    
    **Response:** The response includes information about the sync operation, including how many events were synced. The actual event data is available via the list events endpoint.
    
    Returns 200 with sync results. Returns 401 if OAuth token refresh failed, or 404 if the calendar connection is not found.
 * @summary Sync calendar events
 */
export const syncCalendar = (
  calendarId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<SyncCalendarResponse>(
    { url: `/v2/calendars/${calendarId}/sync`, method: "POST" },
    options
  )
}
/**
 * Renew or recreate the push subscription for a calendar connection.
    
    Useful when subscriptions expire or need to be refreshed. A new subscription will be created and the old one will be cancelled. Subscriptions expire after a certain period (3 days for Microsoft, longer for Google) and need to be renewed periodically.
    
    **Subscription Renewal:** The endpoint creates a new push subscription with the calendar provider. The old subscription is cancelled to prevent duplicate webhooks. The new subscription will send webhooks for all calendar events (created, updated, cancelled).
    
    **Subscription Expiration:** Subscriptions expire automatically after a certain period:
    - Microsoft: 3 days maximum
    - Google: Longer period (varies)
    
    When a subscription expires, you will stop receiving webhook notifications. Use this endpoint to renew the subscription before it expires.
    
    **Use Cases:**
    - Subscription is about to expire
    - Subscription has expired and webhooks stopped working
    - Need to refresh subscription for troubleshooting
    
    **Response:** The response includes information about the new subscription, including when it was created and when it expires.
    
    Returns 200 with subscription information. Returns 401 if OAuth token refresh failed, 403 if permission is denied, or 404 if the calendar connection is not found.
 * @summary Resubscribe to calendar webhooks
 */
export const resubscribeCalendar = (
  calendarId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ResubscribeCalendarResponse>(
    { url: `/v2/calendars/${calendarId}/resubscribe`, method: "POST" },
    options
  )
}
/**
 * Retrieve a paginated list of calendar events.
    
    Supports filtering by date range, status (confirmed, cancelled, tentative), and whether events are deleted. Results include whether a bot is scheduled for each event. Use cursor-based pagination for efficient navigation.
    
    **Pagination:** Uses cursor-based pagination. Provide a `cursor` query parameter to fetch the next page. The `limit` parameter controls how many results are returned per page (default: 50, max: 250).
    
    **Filtering:**
    - `start_after`: ISO 8601 timestamp - only return events starting after this time
    - `start_before`: ISO 8601 timestamp - only return events starting before this time
    - `status`: Filter by event status (confirmed, cancelled, tentative)
    - `include_deleted`: Include deleted events in results (default: false)
    
    **Event Information:** Each event includes its ID, title, start/end times, status, meeting URL (if available), meeting platform (if detected), and whether a bot is scheduled for the event.
    
    **Bot Scheduling:** The `bot_scheduled` field indicates whether a calendar bot schedule exists for this event. This does not mean the bot has joined - it means a bot is scheduled to join when the event starts.
    
    Returns a paginated list of calendar events with metadata.
 * @summary List calendar events
 */
export const listEvents = (
  calendarId: string,
  params: ListEventsParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ListEventsResponse>(
    { url: `/v2/calendars/${calendarId}/events`, method: "GET", params },
    options
  )
}
/**
 * Retrieve a paginated list of event series (both one-off and recurring events).
    
    Each series includes its associated event instances. Supports filtering by event type (one_off, recurring) and whether series are deleted. Use cursor-based pagination for efficient navigation.
    
    **Pagination:** Uses cursor-based pagination. Provide a `cursor` query parameter to fetch the next page. The `limit` parameter controls how many results are returned per page (default: 50, max: 250).
    
    **Event Types:**
    - `one_off`: Single events (not part of a recurring series)
    - `recurring`: Events that are part of a recurring series
    
    **Series Information:** Each series includes its series ID, event type, whether a bot is scheduled for all occurrences (`series_bot_scheduled`), and an array of event instances. For one-off events, the instances array contains a single instance. For recurring events, it contains all instances that have been synced.
    
    **Filtering:**
    - `event_type`: Filter by event type (one_off, recurring)
    - `include_deleted`: Include deleted series in results (default: false)
    
    **Bot Scheduling:** The `series_bot_scheduled` field indicates whether a calendar bot schedule exists for all occurrences of this series. Individual instances may have different bot scheduling status.
    
    Returns a paginated list of event series with their instances.
 * @summary List event series
 */
export const listEventSeries = (
  calendarId: string,
  params: ListEventSeriesParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ListEventSeriesResponse>(
    { url: `/v2/calendars/${calendarId}/series`, method: "GET", params },
    options
  )
}
/**
 * Retrieve detailed information about a specific calendar event.
    
    Returns comprehensive event information including attendees, meeting URL, meeting platform, status, and whether a bot is scheduled. Returns deleted events as well (use the `include_deleted` parameter or check the `deleted_at` field).
    
    **Event Details:** Includes all event metadata:
    - Title, description, location
    - Start and end times (ISO 8601 timestamps)
    - Status (confirmed, cancelled, tentative)
    - Attendees list
    - Meeting URL (if available)
    - Meeting platform (if detected from URL)
    - Whether it's an all-day event
    - Whether it's an exception to a recurring series
    
    **Bot Scheduling:** The `bot_scheduled` field indicates whether a calendar bot schedule exists for this event. If the event is part of a recurring series, the `series_bot_scheduled` field indicates whether a bot is scheduled for all occurrences.
    
    **Deleted Events:** Deleted events are included in the response. Check the `deleted_at` field to determine if an event has been deleted. Deleted events may still have associated bot schedules if they were scheduled before deletion.
    
    Returns 404 if the event is not found or does not belong to the specified calendar.
 * @summary Get event details
 */
export const getEventDetails = (
  calendarId: string,
  eventId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<GetEventDetailsResponse>(
    { url: `/v2/calendars/${calendarId}/events/${eventId}`, method: "GET" },
    options
  )
}
/**
 * Schedule a bot to automatically join a calendar event.
    
    You can schedule for all occurrences of a recurring event or specific event instances. The bot will use the meeting URL from the event. Returns partial success if some events fail to schedule (e.g., if a bot is already scheduled or if the event doesn't have a meeting URL).
    
    **Scheduling Options:**
    - `series_id`: Schedule for all occurrences of a recurring series
    - `event_id`: Schedule for a specific event instance
    - `all_occurrences`: Schedule for all future occurrences (for recurring events)
    
    **Meeting URL Requirement:** The event must have a meeting URL. If the event doesn't have a meeting URL, the scheduling will fail for that event. The meeting platform is automatically detected from the URL.
    
    **Bot Configuration:** You can provide bot configuration (name, image, recording mode, transcription settings, etc.) that will be used for all scheduled bots. The configuration applies to all events you're scheduling for.
    
    **Partial Success:** If you're scheduling for multiple events (e.g., all occurrences of a series), some events may fail to schedule (e.g., if a bot is already scheduled). The response includes information about which events succeeded and which failed.
    
    **Token Reservation:** Tokens are NOT reserved at scheduling time. Token availability and daily bot cap are checked when each bot actually joins the meeting. If tokens are insufficient or the daily cap is reached at join time, the bot will fail with an appropriate error.
    
    **Status:** The calendar bot schedule starts in `scheduled` status and transitions to `completed` when the bot instance is created and queued to join. If the bot fails to join, it transitions to `failed` status.
    
    Returns 201 with scheduling results. Returns 404 if the event series or event is not found.
 * @summary Schedule bot for calendar event
 */
export const createCalendarBot = (
  calendarId: string,
  createCalendarBotRequestBodyInput: BodyType<CreateCalendarBotRequestBodyInput>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<CreateCalendarBotResponse>(
    {
      url: `/v2/calendars/${calendarId}/bots`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: createCalendarBotRequestBodyInput
    },
    options
  )
}
/**
 * Update one or more calendar bots for a calendar.
    
    You can target a single event or all occurrences in a series using `series_id`, `all_occurrences`, and `event_id` in the request body. The bot must be in `scheduled` status and the join time must be at least 4 minutes in the future.
    
    **Update Targets:**
    - `event_id`: Update bot for a specific event instance
    - `series_id`: Update bots for all occurrences of a series
    - `all_occurrences`: Update all future occurrences (for recurring events)
    
    **Updateable Fields:** You can update any bot configuration field (bot name, image, recording mode, transcription settings, etc.). All fields are optional - only provided fields will be updated.
    
    **Status Requirements:** The bot must be in `scheduled` status. Bots that have already joined (`completed`) or failed (`failed`) cannot be updated. If the bot is in an invalid state, the request will fail with a 409 Conflict status.
    
    **Join Time Requirements:** The join time must be at least 4 minutes in the future. If the join time is too close, the request will fail with 409 Conflict. This ensures the bot can be updated before it starts processing.
    
    **Partial Updates:** If updating multiple bots (e.g., all occurrences of a series), some bots may fail to update (e.g., if they're not in `scheduled` status). The response includes information about which bots were updated and which failed.
    
    Returns 200 with update results. Returns 404 if the event or calendar bot schedule is not found, or 409 if the bot's status does not allow update.
 * @summary Update calendar bot
 */
export const updateCalendarBot = (
  calendarId: string,
  updateCalendarBotRequestBodyInput: BodyType<UpdateCalendarBotRequestBodyInput>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<UpdateCalendarBotResponse>(
    {
      url: `/v2/calendars/${calendarId}/bots`,
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      data: updateCalendarBotRequestBodyInput
    },
    options
  )
}
/**
 * Cancel one or more scheduled calendar bots. You can target a single event or all occurrences in a series using `series_id`, `all_occurrences`, and `event_id` in the request body. The behavior for each targeted bot depends on where it is in its lifecycle:

    - **Not yet launched** — the schedule is cancelled and the bot never starts.
    - **Launched but not yet in the meeting** — the bot exits before joining, with an `EXITING_MEETING_BEFORE_RECORD` error code.
    - **Already in the meeting** — the bot leaves. Whatever it captured up to that point is still processed and delivered as usual. This case applies to single-event deletes and to series deletes for recent occurrences; see "Series deletes" below.

    **Cancellation Targets:**
    - `event_id`: Cancel bot for a specific event instance
    - `series_id`: Cancel bots for all occurrences of a series
    - `all_occurrences`: Cancel all future occurrences (for recurring events)

    **Series deletes:** When `all_occurrences` is true, occurrences whose scheduled time is already well in the past are silently skipped — their bots would have long since finished. Recent and future occurrences are cancelled normally. For single-event deletes (`event_id`, no `all_occurrences`), no time filter is applied — whatever state the bot is in, the stop request is delivered.

    **Partial Cancellation:** If cancelling multiple bots (e.g., all occurrences of a series), some bots may fail to cancel. The response includes which bots were cancelled and which failed.

    **Irreversible Operation:** Once a calendar bot is cancelled, it cannot be recovered.

    **Token Impact:** Tokens are not reserved for calendar bots, so cancelling before a bot joins a meeting has no impact on your token balance. If a bot had already started recording, tokens are consumed only for the recorded portion — you won't be charged for time after the cancellation.

    Returns 200 with cancellation results. Returns 404 if the event or calendar bot schedule is not found.
 * @summary Cancel calendar bot
 */
export const deleteCalendarBot = (
  calendarId: string,
  deleteCalendarBotBody: BodyType<DeleteCalendarBotBody>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<DeleteCalendarBotResponse>(
    {
      url: `/v2/calendars/${calendarId}/bots`,
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      data: deleteCalendarBotBody
    },
    options
  )
}
export type ListRawCalendarsResult = NonNullable<Awaited<ReturnType<typeof listRawCalendars>>>
export type CreateCalendarConnectionResult = NonNullable<
  Awaited<ReturnType<typeof createCalendarConnection>>
>
export type ListCalendarsResult = NonNullable<Awaited<ReturnType<typeof listCalendars>>>
export type GetCalendarDetailsResult = NonNullable<Awaited<ReturnType<typeof getCalendarDetails>>>
export type DeleteCalendarConnectionResult = NonNullable<
  Awaited<ReturnType<typeof deleteCalendarConnection>>
>
export type UpdateCalendarConnectionResult = NonNullable<
  Awaited<ReturnType<typeof updateCalendarConnection>>
>
export type SyncCalendarResult = NonNullable<Awaited<ReturnType<typeof syncCalendar>>>
export type ResubscribeCalendarResult = NonNullable<Awaited<ReturnType<typeof resubscribeCalendar>>>
export type ListEventsResult = NonNullable<Awaited<ReturnType<typeof listEvents>>>
export type ListEventSeriesResult = NonNullable<Awaited<ReturnType<typeof listEventSeries>>>
export type GetEventDetailsResult = NonNullable<Awaited<ReturnType<typeof getEventDetails>>>
export type CreateCalendarBotResult = NonNullable<Awaited<ReturnType<typeof createCalendarBot>>>
export type UpdateCalendarBotResult = NonNullable<Awaited<ReturnType<typeof updateCalendarBot>>>
export type DeleteCalendarBotResult = NonNullable<Awaited<ReturnType<typeof deleteCalendarBot>>>
